import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import '../../styles/pages.css';

export default function SendBulkSms() {
  const [sourceType, setSourceType] = useState('group');
  const [messageType, setMessageType] = useState('compose');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [messageText, setMessageText] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileData, setFileData] = useState([]);
  const [contactCount, setContactCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0, percentage: 0 });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const entityId = user.entity;

  const [contactGroups, setContactGroups] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Fetch contact groups and templates on mount
  useEffect(() => {
    async function fetchData() {
      try {
        const { data: groups } = await api.get(`/api/contact-groups/${entityId}`);
        setContactGroups(groups);
        console.log('✓ Contact groups fetched:', groups.length);

        const { data: templateList } = await api.get(`/api/templates/${entityId}`);
        setTemplates(templateList);
        console.log('✓ Templates fetched:', templateList.length);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
    fetchData();
  }, [entityId]);

  // Normalize phone number to 254XXXXXXXXX format
  function normalizePhoneNumber(phone) {
    if (!phone) return null;
    let cleaned = phone.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }
    if (cleaned.startsWith('0')) {
      cleaned = '254' + cleaned.substring(1);
    }
    if (cleaned.startsWith('254')) {
      return cleaned;
    }
    if (cleaned.length === 9) {
      return '254' + cleaned;
    }
    return cleaned;
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const csv = event.target.result;
          const lines = csv.split('\n');

          const headerLine = lines[0].toLowerCase();
          const headers = headerLine.split(',').map(h => h.trim().toLowerCase());
          const msisdnIndex = headers.findIndex(h => h === 'msisdn' || h === 'phone' || h === 'phonenumber');

          if (msisdnIndex === -1) {
            alert('CSV must contain a "msisdn" column');
            setFileName('');
            setFileData([]);
            setContactCount(0);
            return;
          }

          const phoneNumbers = [];

          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line) {
              const fields = line.split(',');
              const phone = fields[msisdnIndex]?.trim();

              if (phone) {
                const normalized = normalizePhoneNumber(phone);
                if (normalized && normalized.startsWith('254') && normalized.length === 12) {
                  phoneNumbers.push(normalized);
                }
              }
            }
          }

          setFileData(phoneNumbers);
          setContactCount(phoneNumbers.length);
          console.log(`✓ Parsed ${phoneNumbers.length} phone numbers from CSV`);
        } catch (error) {
          console.error('Error parsing CSV:', error);
          alert('Error parsing CSV file. Please check the format.');
          setFileName('');
          setFileData([]);
          setContactCount(0);
        }
      };
      reader.readAsText(file);
    }
  }

  function handleTemplateChange(e) {
    const templateId = parseInt(e.target.value);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setMessageText(template.message);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSuccessMessage('');

    if (!messageText.trim()) {
      alert('Please enter a message.');
      return;
    }

    if (messageText.length > 160) {
      alert('Message exceeds 160 characters.');
      return;
    }

    let recipients = [];

    if (sourceType === 'group') {
      if (!selectedGroup) {
        alert('Please select a contact group.');
        return;
      }
      const group = contactGroups.find(g => g.id === parseInt(selectedGroup));
      if (!group) {
        alert('Invalid group selected.');
        return;
      }

      // Fetch contacts from the selected group
      try {
        setLoading(true);
        const { data: allContacts } = await api.get(`/api/contacts/${entityId}`);
        recipients = allContacts
          .filter(c => c.contactGroup === parseInt(selectedGroup))
          .map(c => c.contact);

        if (recipients.length === 0) {
          alert('No contacts found in the selected group.');
          setLoading(false);
          return;
        }

        console.log(`📱 Fetched ${recipients.length} contacts from group: ${group.groupName}`);
      } catch (error) {
        alert('Error fetching contacts from group');
        setLoading(false);
        return;
      }
    } else {
      if (fileData.length === 0) {
        alert('Please upload a CSV file with phone numbers.');
        return;
      }
      recipients = fileData;
    }

    setLoading(true);
    setSendProgress({ current: 0, total: recipients.length, percentage: 0 });

    try {
      console.log(`📱 Sending bulk SMS to ${recipients.length} contacts...`);

      // 1. Queue the campaign. The server accepts it, starts sending in the
      //    background, and returns a jobId immediately — so even 20,000+
      //    recipients never hold this request open.
      const { data: job } = await api.post('/api/sms/send-bulk', {
        entityId,
        recipients,
        message: messageText,
      }, {
        timeout: 60 * 1000,
      });

      console.log('✓ Campaign queued:', job);

      // 2. Poll the server for real sending progress until the job finishes.
      const finalResult = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const { data: status } = await api.get(`/api/sms/campaign-status/${job.jobId}`);
            setSendProgress({
              current: status.processed,
              total: status.total,
              percentage: status.percentage,
            });
            if (status.status === 'completed' || status.status === 'failed') {
              clearInterval(poll);
              resolve(status);
            }
          } catch (pollErr) {
            clearInterval(poll);
            reject(pollErr);
          }
        }, 2000);
      });

      if (finalResult.status === 'failed') {
        throw new Error(finalResult.error || 'Campaign failed while sending.');
      }

      setSendProgress({ current: finalResult.total, total: finalResult.total, percentage: 100 });

      console.log('✓ Bulk SMS sent successfully:', finalResult);
      setSuccessMessage(
        `✓ Campaign sent successfully!\n` +
        `Campaign ID: ${finalResult.campaignId}\n` +
        `Total: ${finalResult.total}\n` +
        `Delivered: ${finalResult.successCount}\n` +
        `Failed: ${finalResult.failCount}`
      );

      // Reset form after 4 seconds
      setTimeout(() => {
        setSourceType('group');
        setMessageType('compose');
        setSelectedGroup('');
        setSelectedTemplate('');
        setMessageText('');
        setFileName('');
        setFileData([]);
        setContactCount(0);
        setSuccessMessage('');
        setSendProgress({ current: 0, total: 0, percentage: 0 });
      }, 4000);
    } catch (err) {
      console.error('Error sending SMS:', err);
      alert(err.response?.data?.message || 'Error sending SMS campaign.');
      setSendProgress({ current: 0, total: 0, percentage: 0 });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Send Bulk SMS</h1>
        <p>Send SMS messages to multiple recipients at once</p>
      </div>

      {successMessage && (
        <div className="success-banner">
          <pre>{successMessage}</pre>
        </div>
      )}

      <div className="send-bulk-wrapper">
        {/* Left Section - Form */}
        <div className="send-bulk-left">
          <form onSubmit={handleSubmit}>
            {/* ── Source Selection ── */}
            <div className="form-section">
              <h3 className="form-section-title">Select Recipients</h3>

              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="sourceType"
                    value="group"
                    checked={sourceType === 'group'}
                    onChange={(e) => setSourceType(e.target.value)}
                  />
                  <span>From Contact Group</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="sourceType"
                    value="file"
                    checked={sourceType === 'file'}
                    onChange={(e) => setSourceType(e.target.value)}
                  />
                  <span>From CSV File</span>
                </label>
              </div>

              {/* Contact Group Selection */}
              {sourceType === 'group' && (
                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                  <label htmlFor="group">Select Contact Group</label>
                  <select
                    id="group"
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    required={sourceType === 'group'}
                  >
                    <option value="">-- Choose a group --</option>
                    {contactGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.groupName} ({group.contactCount} contacts)
                      </option>
                    ))}
                  </select>
                  {selectedGroup && (
                    <p className="group-info">
                      ✓ Selected: {contactGroups.find(g => g.id === parseInt(selectedGroup))?.groupName}
                    </p>
                  )}
                </div>
              )}

              {/* CSV File Upload */}
              {sourceType === 'file' && (
                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                  <label htmlFor="csvFile">Upload CSV File (with msisdn column)</label>
                  <div className="file-upload-wrapper">
                    <input
                      id="csvFile"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="file-input"
                      required={sourceType === 'file'}
                    />
                    <div className="file-upload-area">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      {fileName ? (
                        <div>
                          <p className="file-name">✓ {fileName}</p>
                          <p className="file-hint-success">
                            {contactCount} contacts loaded
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="file-hint">Click to select CSV file</p>
                          <p className="file-hint-small">Formats: 0797083661, 254797083661, +254797083661</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Message Selection ── */}
            <div className="form-section" style={{ marginTop: '2.5rem' }}>
              <h3 className="form-section-title">Message Content</h3>

              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="messageType"
                    value="compose"
                    checked={messageType === 'compose'}
                    onChange={(e) => setMessageType(e.target.value)}
                  />
                  <span>Compose Message</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="messageType"
                    value="template"
                    checked={messageType === 'template'}
                    onChange={(e) => setMessageType(e.target.value)}
                  />
                  <span>Use Template</span>
                </label>
              </div>

              {/* Template Selection */}
              {messageType === 'template' && (
                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                  <label htmlFor="template">Select Template</label>
                  {templates.length === 0 ? (
                    <div className="no-templates-notice">
                      <p>📝 No templates available</p>
                      <p className="small-text">Create templates in the Templates page to use them here</p>
                    </div>
                  ) : (
                    <select
                      id="template"
                      value={selectedTemplate}
                      onChange={handleTemplateChange}
                      required={messageType === 'template'}
                    >
                      <option value="">-- Choose a template --</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.templateName}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Message Text Area */}
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label htmlFor="message">
                  Message Text
                  <span className="char-count">
                    {messageText.length}/160
                  </span>
                </label>
                <textarea
                  id="message"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Enter your message here..."
                  rows="5"
                  maxLength="160"
                  required
                />
                <p className="message-hint">
                  {160 - messageText.length} characters remaining
                </p>
              </div>
            </div>

            {/* ── Submit Button ── */}
            <div style={{ marginTop: '2.5rem', display: 'flex', gap: '1rem' }}>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner" style={{ marginRight: '0.5rem' }} />
                    Sending...
                  </>
                ) : (
                  'Send Campaign'
                )}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setSourceType('group');
                  setMessageType('compose');
                  setSelectedGroup('');
                  setSelectedTemplate('');
                  setMessageText('');
                  setFileName('');
                  setFileData([]);
                  setContactCount(0);
                  setSuccessMessage('');
                  setSendProgress({ current: 0, total: 0, percentage: 0 });
                }}
                disabled={loading}
              >
                Clear Form
              </button>
            </div>
          </form>
        </div>

        {/* Right Section - Stats */}
        <div className="send-bulk-right">
          <div className="stats-panel">
            <h3 className="stats-title">Campaign Stats</h3>

            {/* Contacts Summary */}
            <div className="stats-item">
              <div className="stats-label">Contacts Fetched</div>
              <div className="stats-value">
                {sourceType === 'group'
                  ? contactGroups.find(g => g.id === parseInt(selectedGroup))?.contactCount || 0
                  : contactCount}
              </div>
              <div className="stats-source">
                {sourceType === 'group'
                  ? contactGroups.find(g => g.id === parseInt(selectedGroup))?.groupName || 'No group selected'
                  : fileName || 'No file selected'}
              </div>
            </div>

            {/* Progress Section */}
            {loading && sendProgress.total > 0 && (
              <div className="stats-item">
                <div className="stats-label">Sending Progress</div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${sendProgress.percentage}%` }}
                  />
                </div>
                <div className="progress-text">
                  {sendProgress.current} / {sendProgress.total}
                </div>
                <div className="progress-percentage">
                  {sendProgress.percentage}% Complete
                </div>
              </div>
            )}

            {/* Message Summary */}
            {messageText && (
              <div className="stats-item">
                <div className="stats-label">Message Details</div>
                <div className="stats-info">
                  <div className="info-row">
                    <span>Length:</span>
                    <span>{messageText.length} / 160</span>
                  </div>
                  <div className="info-row">
                    <span>Source:</span>
                    <span>
                      {messageType === 'template'
                        ? templates.find(t => t.id === parseInt(selectedTemplate))?.templateName || 'N/A'
                        : 'Custom'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            {(selectedGroup || contactCount > 0) && messageText && (
              <div className="stats-item">
                <div className="stats-label">Ready to Send</div>
                <div className="ready-badge">
                  ✓ All fields complete
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
